import { useEffect, useMemo, useState } from "react";
import "./RecipeList.css";
import { AuthTokenRetrieve, LoginButton, LogoutButton } from "../Utility/AuthTokenRetrieve";
import { buildApiUrl } from "../Utility/api";

const defaultEditState = {
  quantityText: "",
  quantityValue: "",
  unit: "",
  ingredientNameRaw: "",
  canonicalName: "",
  preparationNote: "",
  reviewNotes: "",
  parseStatus: "parsed",
  reviewStatus: "edited",
  ignoredForAnalysis: false,
};

const IngredientReview = () => {
  const [rows, setRows] = useState([]);
  const [shouldShow, setShouldShow] = useState();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filterMode, setFilterMode] = useState("pending");
  const [searchText, setSearchText] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editState, setEditState] = useState(defaultEditState);
  const [savingId, setSavingId] = useState(null);
  const [statusMessage, setStatusMessage] = useState("");
  const { shouldShow: shouldShowGoogle } = AuthTokenRetrieve();

  useEffect(() => {
    setShouldShow(shouldShowGoogle);
  }, [shouldShowGoogle]);

  useEffect(() => {
    if (!shouldShow) {
      setLoading(false);
      return;
    }

    setLoading(true);
    fetch(buildApiUrl("/food/tracking/review"), { cache: "no-cache" })
      .then((response) => response.json())
      .then((data) => {
        setRows(data || []);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message ? err.message : "Failed to load review data");
        setLoading(false);
      });
  }, [shouldShow]);

  const filteredRows = useMemo(() => {
    return rows.filter((row) => {
      const matchesSearch =
        !searchText ||
        row.recipeName.toLowerCase().includes(searchText.toLowerCase()) ||
        row.rawLine.toLowerCase().includes(searchText.toLowerCase()) ||
        row.ingredientNameRaw.toLowerCase().includes(searchText.toLowerCase()) ||
        row.canonicalName.toLowerCase().includes(searchText.toLowerCase());

      if (!matchesSearch) {
        return false;
      }

      if (filterMode === "all") {
        return true;
      }
      if (filterMode === "lowConfidence") {
        return Number(row.parserConfidence || 0) < 0.75;
      }
      if (filterMode === "ignored") {
        return row.ignoredForAnalysis;
      }

      return row.reviewStatus === filterMode;
    });
  }, [rows, filterMode, searchText]);

  const beginEdit = (row) => {
    setEditingId(row.id);
    setEditState({
      quantityText: row.quantityText || "",
      quantityValue: row.quantityValue ?? "",
      unit: row.unit || "",
      ingredientNameRaw: row.ingredientNameRaw || "",
      canonicalName: row.canonicalName || "",
      preparationNote: row.preparationNote || "",
      reviewNotes: row.reviewNotes || "",
      parseStatus: row.parseStatus || "parsed",
      reviewStatus: row.reviewStatus || "edited",
      ignoredForAnalysis: !!row.ignoredForAnalysis,
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditState(defaultEditState);
  };

  const handleEditChange = (event) => {
    const { name, value, type, checked } = event.target;
    setEditState((currentValue) => ({
      ...currentValue,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const replaceRow = (updatedRow) => {
    setRows((currentRows) =>
      currentRows.map((row) => (row.id === updatedRow.id ? updatedRow : row))
    );
  };

  const saveRow = (rowId, overrides = {}) => {
    setSavingId(rowId);
    setStatusMessage("");

    const payload = {
      ...editState,
      ...overrides,
    };

    fetch(buildApiUrl(`/food/tracking/review/${rowId}`), {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    })
      .then((response) => response.json())
      .then((updatedRow) => {
        replaceRow(updatedRow);
        setSavingId(null);
        setEditingId(null);
        setEditState(defaultEditState);
        setStatusMessage("Ingredient row updated");
      })
      .catch((err) => {
        setSavingId(null);
        setStatusMessage(err.message ? err.message : "Failed to save row");
      });
  };

  const quickUpdate = (row, reviewStatus, ignoredForAnalysis) => {
    setEditingId(row.id);
    setEditState({
      quantityText: row.quantityText || "",
      quantityValue: row.quantityValue ?? "",
      unit: row.unit || "",
      ingredientNameRaw: row.ingredientNameRaw || "",
      canonicalName: row.canonicalName || "",
      preparationNote: row.preparationNote || "",
      reviewNotes: row.reviewNotes || "",
      parseStatus: row.parseStatus || "parsed",
      reviewStatus,
      ignoredForAnalysis,
    });
    saveRow(row.id, {
      quantityText: row.quantityText || "",
      quantityValue: row.quantityValue ?? "",
      unit: row.unit || "",
      ingredientNameRaw: row.ingredientNameRaw || "",
      canonicalName: row.canonicalName || "",
      preparationNote: row.preparationNote || "",
      reviewNotes: row.reviewNotes || "",
      parseStatus: row.parseStatus || "parsed",
      reviewStatus,
      ignoredForAnalysis,
    });
  };

  const deleteRow = (rowId) => {
    setSavingId(rowId);
    setStatusMessage("");
    fetch(buildApiUrl(`/food/tracking/review/${rowId}`), {
      method: "DELETE",
    })
      .then((response) => response.json())
      .then(() => {
        setRows((currentRows) => currentRows.filter((row) => row.id !== rowId));
        setSavingId(null);
        if (editingId === rowId) {
          cancelEdit();
        }
        setStatusMessage("Ingredient row deleted");
      })
      .catch((err) => {
        setSavingId(null);
        setStatusMessage(err.message ? err.message : "Failed to delete row");
      });
  };

  const summary = useMemo(() => {
    const pendingCount = rows.filter((row) => row.reviewStatus === "pending").length;
    const lowConfidenceCount = rows.filter(
      (row) => Number(row.parserConfidence || 0) < 0.75
    ).length;
    const ignoredCount = rows.filter((row) => row.ignoredForAnalysis).length;

    return { pendingCount, lowConfidenceCount, ignoredCount };
  }, [rows]);

  if (!shouldShow) {
    return (
      <div className="searchBarDiv formViewWrapper">
        <LoginButton setShouldShow={setShouldShow}></LoginButton>
      </div>
    );
  }

  return (
    <div className="IngredientReviewPage">
      <div className="searchBarDiv formViewWrapper IngredientReviewControls">
        <div className="IngredientReviewHeaderRow">
          <div>
            <h2 className="IngredientReviewTitle">Parsed Ingredient Review</h2>
            <p className="IngredientReviewSubtitle">
              Review parsed recipe rows, approve the good ones, ignore the noise,
              and fix canonical ingredient mappings.
            </p>
          </div>
          <LogoutButton setShouldShow={setShouldShow}></LogoutButton>
        </div>

        <div className="IngredientReviewSummaryRow">
          <div className="IngredientReviewStat">Rows: {rows.length}</div>
          <div className="IngredientReviewStat">Pending: {summary.pendingCount}</div>
          <div className="IngredientReviewStat">
            Low Confidence: {summary.lowConfidenceCount}
          </div>
          <div className="IngredientReviewStat">Ignored: {summary.ignoredCount}</div>
        </div>

        <div className="IngredientReviewFilterRow">
          <div className="inputDiv">
            <label>Search</label>
            <input
              className="inputBox addInput"
              value={searchText}
              onChange={(event) => setSearchText(event.target.value)}
              placeholder="Recipe, raw line, or ingredient"
            />
          </div>
          <div className="inputDiv">
            <label>Filter</label>
            <select
              className="inputBox addInput"
              value={filterMode}
              onChange={(event) => setFilterMode(event.target.value)}
            >
              <option value="pending">Pending</option>
              <option value="lowConfidence">Low Confidence</option>
              <option value="edited">Edited</option>
              <option value="approved">Approved</option>
              <option value="ignored">Ignored</option>
              <option value="all">All</option>
            </select>
          </div>
        </div>
        {statusMessage ? <p className="IngredientReviewStatus">{statusMessage}</p> : null}
        {error ? <p className="IngredientReviewError">{error}</p> : null}
      </div>

      {loading ? (
        <div className="shoppingListDiv">Loading parsed ingredients...</div>
      ) : (
        <div className="IngredientReviewList">
          {filteredRows.map((row) => {
            const isEditing = editingId === row.id;
            const isSaving = savingId === row.id;

            return (
              <div className="shoppingListDiv IngredientReviewCard" key={row.id}>
                <div className="IngredientReviewCardHeader">
                  <div>
                    <h3 className="IngredientReviewRecipeName">{row.recipeName}</h3>
                    <p className="IngredientReviewRawLine">{row.rawLine}</p>
                  </div>
                  <div className="IngredientReviewMeta">
                    <span className="IngredientReviewPill">
                      Confidence {row.parserConfidence ?? "n/a"}
                    </span>
                    <span className="IngredientReviewPill">{row.reviewStatus}</span>
                  </div>
                </div>

                {isEditing ? (
                  <div className="IngredientReviewEditGrid">
                    <div className="inputDiv">
                      <label>Quantity Text</label>
                      <input
                        className="inputBox addInput"
                        name="quantityText"
                        value={editState.quantityText}
                        onChange={handleEditChange}
                      />
                    </div>
                    <div className="inputDiv">
                      <label>Quantity Value</label>
                      <input
                        className="inputBox addInput"
                        name="quantityValue"
                        value={editState.quantityValue}
                        onChange={handleEditChange}
                      />
                    </div>
                    <div className="inputDiv">
                      <label>Unit</label>
                      <input
                        className="inputBox addInput"
                        name="unit"
                        value={editState.unit}
                        onChange={handleEditChange}
                      />
                    </div>
                    <div className="inputDiv">
                      <label>Raw Ingredient</label>
                      <input
                        className="inputBox addInput"
                        name="ingredientNameRaw"
                        value={editState.ingredientNameRaw}
                        onChange={handleEditChange}
                      />
                    </div>
                    <div className="inputDiv">
                      <label>Canonical Ingredient</label>
                      <input
                        className="inputBox addInput"
                        name="canonicalName"
                        value={editState.canonicalName}
                        onChange={handleEditChange}
                      />
                    </div>
                    <div className="inputDiv">
                      <label>Preparation Note</label>
                      <input
                        className="inputBox addInput"
                        name="preparationNote"
                        value={editState.preparationNote}
                        onChange={handleEditChange}
                      />
                    </div>
                    <div className="inputDiv">
                      <label>Parse Status</label>
                      <select
                        className="inputBox addInput"
                        name="parseStatus"
                        value={editState.parseStatus}
                        onChange={handleEditChange}
                      >
                        <option value="parsed">Parsed</option>
                        <option value="needs_review">Needs Review</option>
                      </select>
                    </div>
                    <div className="inputDiv">
                      <label>Review Status</label>
                      <select
                        className="inputBox addInput"
                        name="reviewStatus"
                        value={editState.reviewStatus}
                        onChange={handleEditChange}
                      >
                        <option value="pending">Pending</option>
                        <option value="approved">Approved</option>
                        <option value="edited">Edited</option>
                        <option value="ignored">Ignored</option>
                      </select>
                    </div>
                    <div className="inputDiv IngredientReviewCheckboxRow">
                      <label>Ignore For Analysis</label>
                      <input
                        type="checkbox"
                        name="ignoredForAnalysis"
                        checked={editState.ignoredForAnalysis}
                        onChange={handleEditChange}
                      />
                    </div>
                    <div className="inputDiv IngredientReviewNotes">
                      <label>Review Notes</label>
                      <textarea
                        className="inputBox textAreaAdd IngredientReviewTextarea"
                        name="reviewNotes"
                        value={editState.reviewNotes}
                        onChange={handleEditChange}
                      />
                    </div>
                  </div>
                ) : (
                  <div className="IngredientReviewReadGrid">
                    <div><strong>Canonical:</strong> {row.canonicalName || "Unmapped"}</div>
                    <div><strong>Parsed:</strong> {row.quantityText || "-"} {row.unit || ""}</div>
                    <div><strong>Raw Ingredient:</strong> {row.ingredientNameRaw || "-"}</div>
                    <div><strong>Prep:</strong> {row.preparationNote || "-"}</div>
                    <div><strong>Tags:</strong> {row.tags.length ? row.tags.join(", ") : "None"}</div>
                    <div><strong>Ignored:</strong> {row.ignoredForAnalysis ? "Yes" : "No"}</div>
                  </div>
                )}

                <div className="IngredientReviewActions">
                  {isEditing ? (
                    <>
                      <button
                        className="summaryButton"
                        disabled={isSaving}
                        onClick={() => saveRow(row.id)}
                      >
                        Save
                      </button>
                      <button className="summaryButton" disabled={isSaving} onClick={cancelEdit}>
                        Cancel
                      </button>
                    </>
                  ) : (
                    <>
                      <button className="summaryButton" onClick={() => beginEdit(row)}>
                        Edit
                      </button>
                      <button
                        className="summaryButton"
                        disabled={isSaving}
                        onClick={() => quickUpdate(row, "approved", false)}
                      >
                        Approve
                      </button>
                      <button
                        className="summaryButton"
                        disabled={isSaving}
                        onClick={() => quickUpdate(row, "ignored", true)}
                      >
                        Ignore
                      </button>
                    </>
                  )}
                  <button
                    className="summaryButton"
                    disabled={isSaving}
                    onClick={() => deleteRow(row.id)}
                  >
                    Delete
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default IngredientReview;
